import React, { useEffect, useMemo, useRef, useState } from 'react';
import AuthService from '../services/authService';
import Spinner from './Spinner';

interface SearchUser {
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  team_id?: number;
  team_name?: string;
}

interface TeamOption {
  id: number;
  team_code?: number;
  team_name: string;
}

interface CacheQueryRow {
  id: number;
  user_id: string;
  query: string;
  createdon: string;
  sport_code?: string | null;
  entity?: string | null;
  search_count?: number | null;
}

interface CacheFilters {
  sport_code: string;
  entity: string;
  query: string;
  days: string;
}

type SortKey = 'user_id' | 'query' | 'sport_code' | 'entity' | 'search_count' | 'createdon';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 50;
const SPORT_OPTIONS = ['', 'MFB', 'MBB', 'WBB', 'MIH', 'WIH', 'MSO', 'WSO', 'MVB', 'WVB'];
const ENTITY_OPTIONS = ['', 'Player', 'Team'];

const SORTABLE_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'user_id', label: 'Username' },
  { key: 'query', label: 'Query' },
  { key: 'sport_code', label: 'Sport Code' },
  { key: 'entity', label: 'Entity' },
  { key: 'search_count', label: 'Search Count' },
  { key: 'createdon', label: 'Created On' },
];

const authService = new AuthService();

const emptyFilters = (): CacheFilters => ({
  sport_code: '',
  entity: '',
  query: '',
  days: '',
});

function compareCacheRows(a: CacheQueryRow, b: CacheQueryRow, key: SortKey, direction: SortDirection): number {
  const multiplier = direction === 'asc' ? 1 : -1;
  const aVal = a[key];
  const bVal = b[key];

  if (key === 'search_count') {
    const aNum = typeof aVal === 'number' ? aVal : Number.NEGATIVE_INFINITY;
    const bNum = typeof bVal === 'number' ? bVal : Number.NEGATIVE_INFINITY;
    return (aNum - bNum) * multiplier;
  }

  if (key === 'createdon') {
    const aTime = aVal ? new Date(String(aVal)).getTime() : 0;
    const bTime = bVal ? new Date(String(bVal)).getTime() : 0;
    return (aTime - bTime) * multiplier;
  }

  const aStr = String(aVal ?? '').toLowerCase();
  const bStr = String(bVal ?? '').toLowerCase();
  return aStr.localeCompare(bStr) * multiplier;
}

const CacheQueriesTab: React.FC<{ refreshTrigger?: number }> = ({ refreshTrigger = 0 }) => {
  const [userNameQuery, setUserNameQuery] = useState('');
  const [userResults, setUserResults] = useState<SearchUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SearchUser[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const [filters, setFilters] = useState<CacheFilters>(emptyFilters());
  const [appliedFilters, setAppliedFilters] = useState<CacheFilters>(emptyFilters());
  const [appliedUsernames, setAppliedUsernames] = useState<string[]>([]);
  const [appliedTeamId, setAppliedTeamId] = useState<number | null>(null);

  const [rows, setRows] = useState<CacheQueryRow[]>([]);
  const [resolvedUsernames, setResolvedUsernames] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);

  const selectedUsernames = useMemo(
    () => selectedUsers.map((u) => u.username).filter(Boolean),
    [selectedUsers]
  );

  const canLoad = selectedUsernames.length > 0 || selectedTeamId != null;

  const selectedTeam = useMemo(
    () => teams.find((t) => t.id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    if (!q) return teams.slice(0, 50);
    return teams
      .filter((t) => t.team_name?.toLowerCase().includes(q) || String(t.id).includes(q))
      .slice(0, 50);
  }, [teams, teamSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
        setShowTeamDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const response = await authService.getAllTeams();
        if (response.status === 200) {
          const raw = Array.isArray(response.data) ? response.data : response.data?.teams || [];
          setTeams(raw);
        }
      } catch (err) {
        console.error('Error fetching teams:', err);
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchTeams();
  }, []);

  useEffect(() => {
    const searchUsers = async () => {
      if (userNameQuery.trim().length < 2) {
        setUserResults([]);
        return;
      }

      try {
        setIsSearchingUsers(true);
        const response = await authService.searchUsers(userNameQuery.trim());
        if (response.status === 200) {
          setUserResults(response.data.users || []);
          setShowUserDropdown(true);
        } else {
          setUserResults([]);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setUserResults([]);
      } finally {
        setIsSearchingUsers(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [userNameQuery]);

  const fetchCacheQueries = async (opts: {
    usernames: string[];
    team_id: number | null;
    filters: CacheFilters;
    offset: number;
  }) => {
    if (opts.usernames.length === 0 && opts.team_id == null) {
      setError('Select at least one user or a team before loading cache queries.');
      return;
    }

    setLoadingRows(true);
    setError('');
    setSuccessMessage('');
    try {
      const daysNum = opts.filters.days.trim() ? Number(opts.filters.days) : undefined;
      const response = await authService.getCacheQueries({
        usernames: opts.usernames,
        team_id: opts.team_id ?? undefined,
        sport_code: opts.filters.sport_code || undefined,
        entity: opts.filters.entity || undefined,
        query: opts.filters.query || undefined,
        days: daysNum != null && Number.isFinite(daysNum) && daysNum >= 1 ? daysNum : undefined,
        limit: PAGE_SIZE,
        offset: opts.offset,
      });

      if (response.status === 200) {
        const data = response.data || {};
        const queries = Array.isArray(data.queries) ? data.queries : [];
        setRows(
          queries
            .map((row: any) => ({
              id: Number(row.id),
              user_id: String(row.user_id ?? ''),
              query: row.query ?? '',
              createdon: row.createdon ?? '',
              sport_code: row.sport_code ?? null,
              entity: row.entity ?? null,
              search_count: row.search_count ?? null,
            }))
            .filter((row: CacheQueryRow) => Number.isInteger(row.id) && row.id > 0)
        );
        setSelectedRowIds(new Set());
        setResolvedUsernames(Array.isArray(data.usernames) ? data.usernames : opts.usernames);
        setHasMore(Boolean(data.pagination?.has_more));
        setOffset(opts.offset);
        setHasLoaded(true);
      } else if (response.status === 400) {
        setError(
          response.data?.detail ||
            response.data?.message ||
            'Provide at least one username or a team with users.'
        );
        setRows([]);
        setSelectedRowIds(new Set());
        setHasMore(false);
      } else if (response.status === 401 || response.status === 403) {
        setError('You must be an app admin to view cache queries.');
        setRows([]);
        setSelectedRowIds(new Set());
        setHasMore(false);
      } else {
        setError(response.data?.detail || response.data?.message || 'Failed to fetch cache queries');
        setRows([]);
        setSelectedRowIds(new Set());
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching cache queries:', err);
      setError('An error occurred while fetching cache queries');
      setRows([]);
      setSelectedRowIds(new Set());
      setHasMore(false);
    } finally {
      setLoadingRows(false);
    }
  };

  const handleLoad = () => {
    if (!canLoad) {
      setError('Select at least one user or a team before loading cache queries.');
      return;
    }
    setAppliedUsernames(selectedUsernames);
    setAppliedTeamId(selectedTeamId);
    setAppliedFilters({ ...filters });
    fetchCacheQueries({
      usernames: selectedUsernames,
      team_id: selectedTeamId,
      filters,
      offset: 0,
    });
  };

  useEffect(() => {
    if (refreshTrigger <= 0 || !hasLoaded) return;
    fetchCacheQueries({
      usernames: appliedUsernames,
      team_id: appliedTeamId,
      filters: appliedFilters,
      offset,
    });
    // Intentionally only re-fetch when Refresh is clicked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const handleUserSelect = (user: SearchUser) => {
    if (!user.username) return;
    setSelectedUsers((prev) => {
      if (prev.some((u) => u.username === user.username)) return prev;
      return [...prev, user];
    });
    setUserNameQuery('');
    setUserResults([]);
    setShowUserDropdown(false);
    setError('');
  };

  const handleRemoveUser = (username: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.username !== username));
  };

  const handleClearAllUsers = () => {
    setSelectedUsers([]);
  };

  const handleSelectTeam = (team: TeamOption) => {
    setSelectedTeamId(team.id);
    setTeamSearch('');
    setShowTeamDropdown(false);
    setError('');
  };

  const handleClearTeam = () => {
    setSelectedTeamId(null);
    setTeamSearch('');
  };

  const goPrev = () => {
    const nextOffset = Math.max(0, offset - PAGE_SIZE);
    fetchCacheQueries({
      usernames: appliedUsernames,
      team_id: appliedTeamId,
      filters: appliedFilters,
      offset: nextOffset,
    });
  };

  const goNext = () => {
    if (!hasMore) return;
    fetchCacheQueries({
      usernames: appliedUsernames,
      team_id: appliedTeamId,
      filters: appliedFilters,
      offset: offset + PAGE_SIZE,
    });
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => compareCacheRows(a, b, sortKey, sortDirection));
  }, [rows, sortKey, sortDirection]);

  const pageSelectableIds = useMemo(
    () => sortedRows.map((row) => row.id).filter((id) => Number.isInteger(id) && id > 0),
    [sortedRows]
  );

  const allPageSelected =
    pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selectedRowIds.has(id));

  const somePageSelected =
    pageSelectableIds.some((id) => selectedRowIds.has(id)) && !allPageSelected;

  const toggleRowSelection = (id: number) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageSelectableIds.forEach((id) => next.delete(id));
      } else {
        pageSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedRowIds).filter((id) => Number.isInteger(id) && id > 0);
    if (ids.length === 0) return;
    if (ids.length > 200) {
      setError('You can delete at most 200 cache queries at a time.');
      return;
    }
    if (!window.confirm(`Delete ${ids.length} selected cache queries?`)) {
      return;
    }

    setIsDeleting(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await authService.deleteCacheQueries(ids);
      if (response.status === 200) {
        const deletedCount = response.data?.deleted ?? ids.length;
        setSuccessMessage(`Deleted ${deletedCount} cache quer${deletedCount === 1 ? 'y' : 'ies'}.`);
        setSelectedRowIds(new Set());
        await fetchCacheQueries({
          usernames: appliedUsernames,
          team_id: appliedTeamId,
          filters: appliedFilters,
          offset,
        });
      } else if (response.status === 400) {
        setError(
          response.data?.detail ||
            response.data?.message ||
            'Invalid delete request. Select 1–200 valid row ids.'
        );
      } else if (response.status === 401 || response.status === 403) {
        setError('You must be an app admin to delete cache queries.');
      } else {
        setError(response.data?.detail || response.data?.message || 'Failed to delete cache queries');
      }
    } catch (err) {
      console.error('Error deleting cache queries:', err);
      setError('An error occurred while deleting cache queries');
    } finally {
      setIsDeleting(false);
    }
  };

  const availableUserResults = userResults.filter(
    (u) => !selectedUsers.some((s) => s.username === u.username)
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Cache Queries</h2>
        <p className="text-gray-600">
          Inspect cached NL queries for selected users and/or an entire team. AQL is never shown.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Users multi-select */}
        <div ref={userDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Users (multi-select)</label>
          <div className="relative">
            <input
              type="text"
              className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by username, email, or full name"
              value={userNameQuery}
              onChange={(e) => {
                setUserNameQuery(e.target.value);
                setShowUserDropdown(true);
              }}
              onFocus={() => setShowUserDropdown(true)}
              autoComplete="off"
            />
            {showUserDropdown && userNameQuery.length > 0 && (
              <div className="mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-20 absolute shadow-lg">
                {isSearchingUsers && userNameQuery.trim().length >= 2 ? (
                  <div className="p-4 flex justify-center items-center">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-600">Searching...</span>
                  </div>
                ) : availableUserResults.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {availableUserResults.map((user) => (
                      <li
                        key={user.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleUserSelect(user)}
                      >
                        <div className="font-medium text-gray-900">{user.username}</div>
                        <div className="text-sm text-gray-600">
                          {[user.full_name, user.email, user.team_name].filter(Boolean).join(' · ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : userNameQuery.trim().length >= 2 ? (
                  <div className="p-4 text-sm text-gray-500">No users found</div>
                ) : (
                  <div className="p-4 text-sm text-gray-500">Type at least 2 characters to search</div>
                )}
              </div>
            )}
          </div>

          {selectedUsers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {selectedUsers.map((user) => (
                <span
                  key={user.username}
                  className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-50 text-blue-800 border border-blue-200 rounded-md"
                >
                  {user.username}
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-900"
                    onClick={() => handleRemoveUser(user.username)}
                    aria-label={`Remove ${user.username}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800 underline"
                onClick={handleClearAllUsers}
              >
                Clear users
              </button>
            </div>
          )}
        </div>

        {/* Team select */}
        <div ref={teamDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team (optional)</label>
          {selectedTeam ? (
            <div className="flex items-center justify-between gap-3 p-2.5 border border-blue-200 bg-blue-50 rounded-md">
              <div>
                <div className="font-medium text-gray-900">{selectedTeam.team_name}</div>
                <div className="text-xs text-gray-600">team_id: {selectedTeam.id}</div>
              </div>
              <button
                type="button"
                onClick={handleClearTeam}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                className="w-full p-2 border rounded-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={loadingTeams ? 'Loading teams...' : 'Search teams by name'}
                value={teamSearch}
                disabled={loadingTeams}
                onChange={(e) => {
                  setTeamSearch(e.target.value);
                  setShowTeamDropdown(true);
                }}
                onFocus={() => setShowTeamDropdown(true)}
                autoComplete="off"
              />
              {showTeamDropdown && (
                <div className="mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-20 absolute shadow-lg">
                  {filteredTeams.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {filteredTeams.map((team) => (
                        <li
                          key={team.id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSelectTeam(team)}
                        >
                          <div className="font-medium text-gray-900">{team.team_name}</div>
                          <div className="text-xs text-gray-500">team_id: {team.id}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-4 text-sm text-gray-500">No teams found</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sport code</label>
          <select
            className="w-full p-2 border rounded-md border-gray-300"
            value={filters.sport_code}
            onChange={(e) => setFilters((prev) => ({ ...prev, sport_code: e.target.value }))}
          >
            {SPORT_OPTIONS.map((opt) => (
              <option key={opt || 'all'} value={opt}>
                {opt || 'All'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Entity</label>
          <select
            className="w-full p-2 border rounded-md border-gray-300"
            value={filters.entity}
            onChange={(e) => setFilters((prev) => ({ ...prev, entity: e.target.value }))}
          >
            {ENTITY_OPTIONS.map((opt) => (
              <option key={opt || 'all'} value={opt}>
                {opt || 'All'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Query contains</label>
          <input
            type="text"
            className="w-full p-2 border rounded-md border-gray-300"
            placeholder="e.g. points"
            value={filters.query}
            onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last N days</label>
          <input
            type="number"
            min={1}
            className="w-full p-2 border rounded-md border-gray-300"
            placeholder="e.g. 30"
            value={filters.days}
            onChange={(e) => setFilters((prev) => ({ ...prev, days: e.target.value }))}
          />
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleLoad}
          disabled={!canLoad || loadingRows}
          className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
            !canLoad || loadingRows
              ? 'bg-blue-300 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loadingRows ? 'Loading...' : 'Load cache queries'}
        </button>
        <button
          type="button"
          onClick={handleDeleteSelected}
          disabled={selectedRowIds.size === 0 || isDeleting || loadingRows}
          className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
            selectedRowIds.size === 0 || isDeleting || loadingRows
              ? 'bg-red-300 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {isDeleting
            ? 'Deleting...'
            : `Delete selected${selectedRowIds.size > 0 ? ` (${selectedRowIds.size})` : ''}`}
        </button>
        {!canLoad && (
          <span className="text-sm text-gray-500">Select at least one user or a team</span>
        )}
      </div>

      {resolvedUsernames.length > 0 && hasLoaded && (
        <div className="mb-3 text-sm text-gray-600">
          Resolved usernames ({resolvedUsernames.length}):{' '}
          <span className="font-medium text-gray-800">{resolvedUsernames.join(', ')}</span>
        </div>
      )}

      {!hasLoaded ? (
        <div className="text-center p-10 bg-gray-50 rounded-lg text-gray-500">
          Choose users and/or a team, set filters, then load cache queries
        </div>
      ) : loadingRows ? (
        <div className="flex justify-center items-center h-40">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center p-10 bg-gray-50 rounded-lg text-gray-500">
          No cache queries found for the current selection
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full min-w-max text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border px-3 py-2 text-left font-semibold w-12">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected;
                      }}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Select all on this page"
                    />
                  </th>
                  {SORTABLE_COLUMNS.map(({ key, label }) => (
                    <th
                      key={key}
                      className="border px-3 py-2 text-left font-semibold cursor-pointer select-none hover:bg-gray-200"
                      onClick={() => handleSort(key)}
                      aria-sort={
                        sortKey === key
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        <span className="text-xs text-gray-500">
                          {sortKey === key ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="border px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedRowIds.has(row.id)}
                        onChange={() => toggleRowSelection(row.id)}
                        aria-label={`Select cache query ${row.id}`}
                      />
                    </td>
                    <td className="border px-3 py-2 whitespace-nowrap">{row.user_id || '-'}</td>
                    <td className="border px-3 py-2 max-w-md whitespace-pre-wrap break-words">
                      {row.query || '-'}
                    </td>
                    <td className="border px-3 py-2">{row.sport_code ?? '-'}</td>
                    <td className="border px-3 py-2">{row.entity ?? '-'}</td>
                    <td className="border px-3 py-2">{row.search_count ?? '-'}</td>
                    <td className="border px-3 py-2 whitespace-nowrap">
                      {row.createdon ? new Date(row.createdon).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {offset + 1}–{offset + rows.length}
              {hasMore ? ' (more available)' : ''}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={offset === 0 || loadingRows}
                className={`px-3 py-1.5 text-sm border rounded-md ${
                  offset === 0 || loadingRows
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={!hasMore || loadingRows}
                className={`px-3 py-1.5 text-sm border rounded-md ${
                  !hasMore || loadingRows
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CacheQueriesTab;
